import {
    IEquityMovementScheduleDates,
    IEquityMovementScheduleFli,
    IEquityMovementSchedulePrintPreviewTableRow,
    IEquityMovementScheduleTableEntryFlis,
} from '@modeso/aumico-lib-annualreport-fe';
import { EMPTY_ARRAY_LENGTH, EMPTY_STRING, FIRST_ELEMENT_INDEX, NumberUtils } from '@modeso/aumico-lib-core-fe';
import {
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    IEquityMovement,
    IEquityMovementEntry,
    IEquityMovementSchedule,
    IGeneralInformation,
    ItemType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { cloneDeep } from 'lodash-es';
import { v4 as uuidv4 } from 'uuid';

const DATE_OPTIONS: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
const INITIAL_NUMBER_OF_MOVEMENTS = 3;
const TOTAL_COLUMN_ID_PREFIX = 'TOTAL_';

export interface IEmsRowTitles {
    previousOpeningTitle?: string | null;
    previousClosingTitle?: string | null;
    currentOpeningTitle?: string | null;
    currentClosingTitle?: string | null;
}

export interface IEmsPrintPeriodRows {
    opening: IEquityMovementSchedulePrintPreviewTableRow;
    movements: IEquityMovementSchedulePrintPreviewTableRow[];
    closing: IEquityMovementSchedulePrintPreviewTableRow;
}

export class EquityMovementScheduleHelper {
    public static normalizeEntries(savedEntries: IEquityMovementEntry[]): IEquityMovementEntry[] {
        if (savedEntries.length === EMPTY_ARRAY_LENGTH) {
            return [];
        }

        const isLegacy = savedEntries.some((entry) => entry.customColType == null);
        if (!isLegacy) {
            return cloneDeep<IEquityMovementEntry[]>(savedEntries);
        }

        const normalizedEntries: IEquityMovementEntry[] = cloneDeep<IEquityMovementEntry[]>(savedEntries).map(
            (entry): IEquityMovementEntry => ({ ...entry, customColType: 'SYSTEM' }),
        );

        const systemFliNames = normalizedEntries.map((entry) => entry.fliName);
        normalizedEntries.push(this.buildDefaultTotalEntry(normalizedEntries[FIRST_ELEMENT_INDEX], systemFliNames));

        return normalizedEntries;
    }

    public static resolveEmsRowTitles(
        schedule: IEquityMovementSchedule,
        dates: IEquityMovementScheduleDates,
        hasNoPreviousData: boolean,
    ): IEmsRowTitles {
        const storedTitles: IEmsRowTitles = {
            previousOpeningTitle: schedule.previousOpeningTitle,
            previousClosingTitle: schedule.previousClosingTitle,
            currentOpeningTitle: schedule.currentOpeningTitle,
            currentClosingTitle: schedule.currentClosingTitle,
        };
        if (schedule.previousClosingTitle !== undefined) {
            return storedTitles;
        }

        const previousOpening = this.filledTitle(schedule.previousOpeningTitle);
        const currentOpening = this.filledTitle(schedule.currentOpeningTitle);
        const currentClosing = this.filledTitle(schedule.currentClosingTitle);
        if (previousOpening == null || currentOpening == null || currentClosing == null) {
            return storedTitles;
        }

        if (hasNoPreviousData) {
            return {
                ...storedTitles,
                currentOpeningTitle: this.withTitleDate(previousOpening, dates.previousYearOpeningDate),
                currentClosingTitle: this.withTitleDate(currentClosing, dates.currentYearClosingDate),
            };
        }

        return {
            previousOpeningTitle: this.withTitleDate(previousOpening, dates.previousYearOpeningDate),
            previousClosingTitle: this.withTitleDate(currentOpening, dates.previousYearClosingDate),
            currentOpeningTitle: this.withTitleDate(currentOpening, dates.currentYearOpeningDate),
            currentClosingTitle: this.withTitleDate(currentClosing, dates.currentYearClosingDate),
        };
    }

    private static filledTitle(title: string | null | undefined): string | null {
        return typeof title === 'string' && title !== EMPTY_STRING ? title : null;
    }

    private static withTitleDate(title: string, date: string | undefined): string {
        return date != null && date !== EMPTY_STRING ? `${title} ${date}` : title;
    }

    public static computeStructureDiff(
        normalizedEntries: IEquityMovementEntry[],
        mappingFlis: IEquityMovementScheduleFli[],
    ): { newFliNames: string[]; removedFliNames: string[] } {
        const savedSystemFliNames = new Set<string>(
            normalizedEntries.filter((entry) => entry.customColType === 'SYSTEM').map((entry) => entry.fliName),
        );
        const mappingFliNames = new Set<string>(mappingFlis.map((fli) => fli.fliName));

        const newFliNames = [...mappingFliNames].filter((fliName) => !savedSystemFliNames.has(fliName));
        const removedFliNames = [...savedSystemFliNames].filter((fliName) => !mappingFliNames.has(fliName));

        return { newFliNames, removedFliNames };
    }

    private static buildDefaultTotalEntry(
        referenceEntry: IEquityMovementEntry,
        sumUpTotalColIDs: string[],
    ): IEquityMovementEntry {
        const previousMovements: IEquityMovement[] = referenceEntry.previousMovements.map((movement) => ({
            name: movement.name,
            value: null,
        }));
        const currentMovements: IEquityMovement[] = referenceEntry.currentMovements.map((movement) => ({
            name: movement.name,
            value: null,
        }));

        return {
            fliName: `${TOTAL_COLUMN_ID_PREFIX}${uuidv4()}`,
            customColType: 'TOTAL',
            sumUpTotalColIDs,
            currentMovements,
            previousMovements,
        };
    }

    public static getMovementScheduleFliInputData(
        equityGroupFlis: IEquityMovementScheduleFli[],
        savedEquityMovementSchedule: IEquityMovementSchedule | null,
    ): IEquityMovementScheduleFli[] {
        const currentEquityGroupFlis = new Array<IEquityMovementScheduleFli>();
        equityGroupFlis.forEach((fli) => {
            if (
                !NumberUtils.isZero(fli.currentYearClosingBalance) ||
                !NumberUtils.isZero(fli.currentYearOpeningBalance)
            ) {
                currentEquityGroupFlis.push(fli);
            }
        });

        if (savedEquityMovementSchedule == null) {
            return currentEquityGroupFlis;
        }

        const fliSavedNames = new Set<string>();
        savedEquityMovementSchedule.equityMovementEntries.forEach((movementEntry) =>
            fliSavedNames.add(movementEntry.fliName),
        );
        const movementScheduleInputFlis: IEquityMovementScheduleFli[] = currentEquityGroupFlis.filter((fli) =>
            fliSavedNames.has(fli.fliName),
        );

        return cloneDeep<IEquityMovementScheduleFli[]>(movementScheduleInputFlis);
    }

    public static getMovementScheduleFlisForAutofill(
        currentEquityGroupFlis: IEquityMovementScheduleFli[],
        previousEquityGroupFlis: IEquityMovementScheduleFli[],
    ): IEquityMovementScheduleFli[] {
        const previousEquityGroupFliNameToFliData = new Map<string, IEquityMovementScheduleFli>();
        previousEquityGroupFlis.forEach((fli) => {
            if (
                !NumberUtils.isZero(fli.currentYearClosingBalance) &&
                !NumberUtils.isZero(fli.currentYearOpeningBalance)
            ) {
                previousEquityGroupFliNameToFliData.set(fli.fliName, fli);
            }
        });

        const currentEquityGroupFliNameToFliData = new Map<string, IEquityMovementScheduleFli>();
        currentEquityGroupFlis.forEach((fli) => {
            if (
                !NumberUtils.isZero(fli.currentYearClosingBalance) &&
                !NumberUtils.isZero(fli.currentYearOpeningBalance)
            ) {
                currentEquityGroupFliNameToFliData.set(fli.fliName, fli);
            }
        });

        const currentAndPreviousEquityGroupFlis: IEquityMovementScheduleFli[] = cloneDeep<IEquityMovementScheduleFli[]>(
            currentEquityGroupFlis.concat(previousEquityGroupFlis),
        );

        const filteredEquityGroupFlis = currentAndPreviousEquityGroupFlis.filter(
            (value, index, currentAndPreviousEquityGroupFlis) => {
                return (
                    currentAndPreviousEquityGroupFlis.findIndex(
                        (fli) =>
                            fli.fliName === value.fliName &&
                            !NumberUtils.isZero(fli.currentYearClosingBalance) &&
                            !NumberUtils.isZero(fli.currentYearOpeningBalance),
                    ) === index
                );
            },
        );

        filteredEquityGroupFlis.forEach((fli: IEquityMovementScheduleFli) => {
            const fliFromPreviousEquityGroup = previousEquityGroupFliNameToFliData.get(fli.fliName);
            const fliFromCurrentEquityGroup = currentEquityGroupFliNameToFliData.get(fli.fliName);

            if (fliFromPreviousEquityGroup != null && fliFromCurrentEquityGroup != null) {
                fli.previousYearOpeningBalance = fliFromPreviousEquityGroup.currentYearOpeningBalance;
            } else if (fliFromPreviousEquityGroup != null) {
                fli.previousYearOpeningBalance = fli.currentYearOpeningBalance;
                fli.currentYearOpeningBalance = 0;
                fli.currentYearClosingBalance = 0;
            } else {
                fli.previousYearOpeningBalance = 0;
            }
        });
        return filteredEquityGroupFlis;
    }

    public static getAutofilledEquityMovementScheduleEntries(
        flis: IEquityMovementScheduleFli[],
        previousEquityMovementScheduleEntries: IEquityMovementEntry[],
    ): IEquityMovementEntry[] {
        const autofilledEquityMovementEntries: IEquityMovementEntry[] = [];

        const fliNameToPreviousYearCurrentMovements = new Map<string, IEquityMovement[]>();

        previousEquityMovementScheduleEntries.forEach((entry) => {
            fliNameToPreviousYearCurrentMovements.set(entry.fliName, entry.currentMovements);
        });

        let emptyFliPreviousMovementsEntryPlaceholder: IEquityMovement[] = [];
        previousEquityMovementScheduleEntries[FIRST_ELEMENT_INDEX].currentMovements.forEach((movement) => {
            emptyFliPreviousMovementsEntryPlaceholder.push({
                name: movement.name,
                value: null,
            });
        });

        for (const fli of flis) {
            let fliPreviousMovements: IEquityMovement[] | undefined = fliNameToPreviousYearCurrentMovements.get(
                fli.fliName,
            );
            if (fliPreviousMovements == null) {
                fliPreviousMovements = emptyFliPreviousMovementsEntryPlaceholder;
            }
            const previousYearOpening: IEquityMovement = {
                name: null,
                value: fli.previousYearOpeningBalance != null ? fli.previousYearOpeningBalance : null,
            };

            const autofilledPreviousMovements: IEquityMovement[] = [];
            autofilledPreviousMovements.push(previousYearOpening);
            autofilledPreviousMovements.push(...fliPreviousMovements);
            autofilledEquityMovementEntries.push({
                fliName: fli.fliName,
                customColType: 'SYSTEM',
                previousMovements: autofilledPreviousMovements,
                currentMovements: this.buildEmptyMovements(),
            });
        }

        // The user's own columns have no mapping FLI, so the loop above skips them. Carry them
        // over with the same one-year shift the SYSTEM columns get: last year's current-year
        // movements become this year's previous-year movements, and last year's closing balance
        // (opening + all movements) becomes the new previous-year opening.
        previousEquityMovementScheduleEntries
            .filter((entry) => entry.customColType === 'USERDEFINED')
            .forEach((entry) => {
                const carriedOpening: IEquityMovement = {
                    name: null,
                    value: NumberUtils.sumIgnoringInfiniteNumbers(
                        entry.previousMovements.map((movement) => movement.value),
                        0,
                    ),
                };
                autofilledEquityMovementEntries.push({
                    fliName: entry.fliName,
                    customColType: 'USERDEFINED',
                    customColumnName: entry.customColumnName,
                    previousMovements: [
                        carriedOpening,
                        ...entry.currentMovements.map((movement) => ({ name: movement.name, value: movement.value })),
                    ],
                    currentMovements: this.buildEmptyMovements(),
                });
            });

        // Every entry above is explicitly typed, so normalizeEntries no longer sees this as legacy
        // data and will NOT add a default TOTAL — this function has to provide it. Carry the user's
        // total configuration if there was one, dropping references to columns that no longer exist.
        const valueColumnIds = autofilledEquityMovementEntries.map((entry) => entry.fliName);
        const reference = autofilledEquityMovementEntries[FIRST_ELEMENT_INDEX];
        const carriedTotals = previousEquityMovementScheduleEntries.filter(
            (entry) => entry.customColType === 'TOTAL',
        );
        if (carriedTotals.length === EMPTY_ARRAY_LENGTH) {
            autofilledEquityMovementEntries.push(this.buildDefaultTotalEntry(reference, valueColumnIds));
        } else {
            carriedTotals.forEach((total) => {
                const sumUpTotalColIDs = (total.sumUpTotalColIDs ?? []).filter((id) => valueColumnIds.includes(id));
                const carried = this.buildDefaultTotalEntry(reference, sumUpTotalColIDs);
                carried.fliName = total.fliName;
                carried.customColumnName = total.customColumnName;
                autofilledEquityMovementEntries.push(carried);
            });
        }

        return autofilledEquityMovementEntries;
    }

    private static buildEmptyMovements(): IEquityMovement[] {
        return Array.from({ length: INITIAL_NUMBER_OF_MOVEMENTS }, () => ({ name: null, value: null }));
    }

    public static extractFlisFromEquityGroup(
        groups: IAccountMappingTemplateGroup[],
        equityGroupName: string,
    ): IEquityMovementScheduleFli[] {
        if (groups.length === EMPTY_ARRAY_LENGTH) {
            return [];
        }

        for (const group of groups) {
            if (group.name === equityGroupName) {
                return this.getFlisOfEquityGroup(group);
            }

            const flis = this.extractFlisFromEquityGroup(group.groups, equityGroupName);
            if (flis.length > EMPTY_ARRAY_LENGTH) {
                return flis;
            }
        }
        return [];
    }

    private static getFlisOfEquityGroup(group: IAccountMappingTemplateGroup): IEquityMovementScheduleFli[] {
        if (group.items.length === EMPTY_ARRAY_LENGTH) {
            return [];
        }
        const flis = group.sortedItems.filter(
            (item) => item.type !== ItemType.SUB_TOTAL && item.type !== ItemType.EMPTY && item.type !== ItemType.HEADER,
        ) as IAccountMappingTemplateGroupItemFLI[];
        const equityMovementScheduleFlis: IEquityMovementScheduleFli[] = flis.map((fli) => {
            const closingReClass = fli.closingReClass != null ? fli.closingReClass : 0;
            const openingReClass = fli.openingReClass != null ? fli.openingReClass : 0;
            const calculatedOpening = fli.calculatedOpeningBalance != null ? fli.calculatedOpeningBalance : 0;
            const calculatedClosing = fli.calculatedClosingBalance != null ? fli.calculatedClosingBalance : 0;
            return {
                fliName: fli.name,
                fliDescriptionToDisplay: fli.caption,
                currentYearOpeningBalance: calculatedOpening + openingReClass,
                currentYearClosingBalance: calculatedClosing + closingReClass,
            };
        });
        return equityMovementScheduleFlis;
    }

    public static prepareEquityMovementScheduleDates(
        annualReportGeneralInformation: IGeneralInformation,
        locale: string,
    ): IEquityMovementScheduleDates {
        const dateTimeFormat = new Intl.DateTimeFormat(locale, DATE_OPTIONS);
        // Equity balances are stated at the BALANCE SHEET date, not at the end of the income
        // statement period; the two normally coincide but the balance date is the authoritative one.
        const localizedCurrentYearClosingDate = dateTimeFormat.format(
            new Date(annualReportGeneralInformation.balanceSheetDate),
        );

        let localizedCurrentYearOpeningDate: string | undefined;
        let localizedPreviousYearOpeningDate: string;
        let localizedPreviousYearClosingDate: string | undefined;
        let previousPeriodIsFullYear = false;

        if (!annualReportGeneralInformation.noPreviousData) {
            const currentYearOpeningDate = new Date(annualReportGeneralInformation.incomeCurrentYearFromDate);
            localizedCurrentYearOpeningDate = dateTimeFormat.format(currentYearOpeningDate);
            const previousYearOpeningDate = new Date(annualReportGeneralInformation.incomePreviousYearFromDate);
            localizedPreviousYearOpeningDate = dateTimeFormat.format(previousYearOpeningDate);
            localizedPreviousYearClosingDate = dateTimeFormat.format(
                new Date(annualReportGeneralInformation.balancePreviousYearDate),
            );
            previousPeriodIsFullYear = this.spansFullYear(
                annualReportGeneralInformation.incomePreviousYearFromDate,
                annualReportGeneralInformation.balancePreviousYearDate,
            );
        } else {
            const previousYearOpeningDate = new Date(annualReportGeneralInformation.incomeCurrentYearFromDate);
            localizedPreviousYearOpeningDate = dateTimeFormat.format(previousYearOpeningDate);
        }

        return {
            currentYearClosingDate: localizedCurrentYearClosingDate,
            currentYearOpeningDate: localizedCurrentYearOpeningDate,
            previousYearOpeningDate: localizedPreviousYearOpeningDate,
            previousYearClosingDate: localizedPreviousYearClosingDate,
            currentPeriodIsFullYear: this.spansFullYear(
                annualReportGeneralInformation.incomeCurrentYearFromDate,
                annualReportGeneralInformation.balanceSheetDate,
            ),
            previousPeriodIsFullYear,
        };
    }

    /** True when from→to covers exactly one year, i.e. a regular business year rather than a
     *  quarter or a shortened period. Used to decide the wording of the period header. */
    private static spansFullYear(fromIsoDate: string, toIsoDate: string): boolean {
        const from = new Date(fromIsoDate);
        const to = new Date(toIsoDate);
        if (isNaN(from.valueOf()) || isNaN(to.valueOf())) {
            return false;
        }
        const expectedTo = new Date(from);
        expectedTo.setFullYear(expectedTo.getFullYear() + 1);
        expectedTo.setDate(expectedTo.getDate() - 1);
        return expectedTo.toDateString() === to.toDateString();
    }

    public static getCurrentYearMovements(
        equityMovementScheduleEntries: IEquityMovementEntry[],
    ): IEquityMovementSchedulePrintPreviewTableRow[] {
        const currentYearMovementsTableEntries: IEquityMovementSchedulePrintPreviewTableRow[] = [];
        const currentMovementNames = equityMovementScheduleEntries[FIRST_ELEMENT_INDEX].currentMovements.map(
            (movement) => movement.name,
        );

        for (const movementName of currentMovementNames) {
            const equityMovementScheduleTableEntry: IEquityMovementSchedulePrintPreviewTableRow = {
                flis: {},
                movementName,
                rowTotal: null,
            };
            currentYearMovementsTableEntries.push(equityMovementScheduleTableEntry);
        }

        const fliNameToCurrentValues = new Map<string, Array<number | null>>();

        for (const equityMovementEntry of equityMovementScheduleEntries) {
            const currentFliValues = equityMovementEntry.currentMovements.map((movement) => movement.value);

            fliNameToCurrentValues.set(equityMovementEntry.fliName, currentFliValues);
        }

        currentYearMovementsTableEntries.forEach((currentYearMovementsTableEntry, index: number) => {
            fliNameToCurrentValues.forEach((values: Array<number | null>, fliName: string) => {
                currentYearMovementsTableEntry.flis[fliName] = values[index];
                currentYearMovementsTableEntry.rowTotal = NumberUtils.sumIgnoringInfiniteNumbers(
                    [currentYearMovementsTableEntry.rowTotal, values[index]],
                    0,
                );
            });
        });

        return currentYearMovementsTableEntries;
    }

    public static getPreviousYearMovements(
        equityMovementSchedule: IEquityMovementEntry[],
    ): IEquityMovementSchedulePrintPreviewTableRow[] {
        const previousYearMovementsTableEntries: IEquityMovementSchedulePrintPreviewTableRow[] = [];
        const previousMovementNames = equityMovementSchedule[FIRST_ELEMENT_INDEX].previousMovements.map(
            (movement) => movement.name,
        );

        for (const movementName of previousMovementNames) {
            const equityMovementScheduleTableEntry: IEquityMovementSchedulePrintPreviewTableRow = {
                flis: {},
                movementName,
                rowTotal: null,
            };
            previousYearMovementsTableEntries.push(equityMovementScheduleTableEntry);
        }

        const fliNameToPreviousValues = new Map<string, Array<number | null>>();

        for (const equityMovementEntry of equityMovementSchedule) {
            const previousFliValues = equityMovementEntry.previousMovements.map((movement) => movement.value);

            fliNameToPreviousValues.set(equityMovementEntry.fliName, previousFliValues);
        }

        previousYearMovementsTableEntries.forEach((previousYearMovementsTableEntry, index: number) => {
            fliNameToPreviousValues.forEach((values: Array<number | null>, fliName: string) => {
                previousYearMovementsTableEntry.flis[fliName] = values[index];

                previousYearMovementsTableEntry.rowTotal = NumberUtils.sumIgnoringInfiniteNumbers(
                    [previousYearMovementsTableEntry.rowTotal, values[index]],
                    0,
                );
            });
        });

        return previousYearMovementsTableEntries;
    }

    public static getCurrentYearClosingRow(
        equityFlis: IEquityMovementScheduleFli[],
        dates: IEquityMovementScheduleDates | undefined,
    ): IEquityMovementSchedulePrintPreviewTableRow {
        const equityFlisCurrentYearClosingValues: IEquityMovementScheduleTableEntryFlis = {};
        let rowTotal = 0;
        equityFlis.forEach((fli) => {
            equityFlisCurrentYearClosingValues[fli.fliName] = fli.currentYearClosingBalance;
            rowTotal = NumberUtils.sumIgnoringInfiniteNumbers([rowTotal, fli.currentYearClosingBalance], 0);
        });

        return {
            movementName: dates != null ? dates.currentYearClosingDate : null,
            flis: equityFlisCurrentYearClosingValues,
            rowTotal,
        };
    }

    public static getCurrentYearOpeningRow(
        equityFlis: IEquityMovementScheduleFli[],
        dates: IEquityMovementScheduleDates | undefined,
    ): IEquityMovementSchedulePrintPreviewTableRow {
        const equityFlisCurrentYearOpeningValues: IEquityMovementScheduleTableEntryFlis = {};
        let rowTotal = 0;
        equityFlis.forEach((fli) => {
            equityFlisCurrentYearOpeningValues[fli.fliName] = fli.currentYearOpeningBalance;
            rowTotal = NumberUtils.sumIgnoringInfiniteNumbers([rowTotal, fli.currentYearOpeningBalance], 0);
        });

        let movementName: string | null = null;
        if (dates != null && dates.currentYearOpeningDate != null) {
            movementName = dates.currentYearOpeningDate;
        }

        return {
            movementName,
            flis: equityFlisCurrentYearOpeningValues,
            rowTotal,
        };
    }

    public static getFliColumnNames(equityFlis: IEquityMovementScheduleFli[]): string[] {
        const equityFlisClonedObject: IEquityMovementScheduleFli[] =
            cloneDeep<IEquityMovementScheduleFli[]>(equityFlis);
        return equityFlisClonedObject.map((fli) => fli.fliDescriptionToDisplay);
    }

    /** null only for an unnamed TOTAL column — the mapper substitutes its localized default title. */
    public static buildEmsPrintColumnCaptions(
        normalizedEntries: IEquityMovementEntry[],
        mappingFlis: IEquityMovementScheduleFli[],
    ): Array<string | null> {
        const fliByName = new Map<string, IEquityMovementScheduleFli>(
            mappingFlis.map((fli) => [fli.fliName, fli]),
        );
        return normalizedEntries.map((entry) => {
            if (entry.customColumnName != null) {
                return entry.customColumnName;
            }
            if ((entry.customColType ?? 'SYSTEM') === 'TOTAL') {
                return null;
            }
            return fliByName.get(entry.fliName)?.fliDescriptionToDisplay ?? entry.fliName;
        });
    }

    /** Mirrors the edit page's period math; previousMovements[0] is the stored opening.
     *  Pass entries through normalizeEntries() first. */
    public static buildEmsPrintPeriodRows(
        normalizedEntries: IEquityMovementEntry[],
        mappingFlis: IEquityMovementScheduleFli[],
        period: 'previous' | 'current',
        hasNoPreviousData: boolean,
    ): IEmsPrintPeriodRows {
        const entryById = new Map<string, IEquityMovementEntry>(
            normalizedEntries.map((entry) => [entry.fliName, entry]),
        );
        const fliByName = new Map<string, IEquityMovementScheduleFli>(
            mappingFlis.map((fli) => [fli.fliName, fli]),
        );
        const isTotal = (entry: IEquityMovementEntry): boolean => (entry.customColType ?? 'SYSTEM') === 'TOTAL';

        const movementValuesOf = (entry: IEquityMovementEntry): Array<number | null> =>
            period === 'previous'
                ? entry.previousMovements.slice(1).map((movement) => movement.value)
                : entry.currentMovements.map((movement) => movement.value);

        const openingOf = (entry: IEquityMovementEntry): number | null => {
            const type = entry.customColType ?? 'SYSTEM';
            const fli = fliByName.get(entry.fliName);
            if (period === 'previous') {
                return fli?.previousYearOpeningBalance ?? entry.previousMovements[FIRST_ELEMENT_INDEX]?.value ?? null;
            }
            if (hasNoPreviousData) {
                return entry.previousMovements[FIRST_ELEMENT_INDEX]?.value ?? null;
            }
            if (type === 'USERDEFINED') {
                return NumberUtils.sumIgnoringInfiniteNumbers(
                    entry.previousMovements.map((movement) => movement.value),
                    0,
                );
            }
            return fli?.currentYearOpeningBalance ?? null;
        };

        const closingOf = (entry: IEquityMovementEntry): number | null => {
            const type = entry.customColType ?? 'SYSTEM';
            if (type === 'SYSTEM') {
                const fli = fliByName.get(entry.fliName);
                return (period === 'previous' ? fli?.currentYearOpeningBalance : fli?.currentYearClosingBalance) ?? null;
            }
            return NumberUtils.sumIgnoringInfiniteNumbers([openingOf(entry), ...movementValuesOf(entry)], 0);
        };

        const totalCell = (
            totalEntry: IEquityMovementEntry,
            valueOf: (entry: IEquityMovementEntry) => number | null,
        ): number =>
            NumberUtils.sumIgnoringInfiniteNumbers(
                (totalEntry.sumUpTotalColIDs ?? [])
                    .map((id) => entryById.get(id))
                    .filter((entry) => entry != null && !isTotal(entry))
                    .map((entry) => valueOf(entry)),
                0,
            );

        const buildRow = (
            movementName: string | null,
            valueOf: (entry: IEquityMovementEntry) => number | null,
        ): IEquityMovementSchedulePrintPreviewTableRow => {
            const flis: IEquityMovementScheduleTableEntryFlis = {};
            normalizedEntries.forEach((entry) => {
                flis[entry.fliName] = isTotal(entry) ? totalCell(entry, valueOf) : valueOf(entry);
            });
            return { movementName, flis, rowTotal: null };
        };

        // Row names are stored redundantly per column; read them from the first value column.
        const referenceEntry = normalizedEntries.find((entry) => !isTotal(entry));
        const movementNames =
            referenceEntry != null
                ? (period === 'previous'
                      ? referenceEntry.previousMovements.slice(1)
                      : referenceEntry.currentMovements
                  ).map((movement) => movement.name)
                : [];

        return {
            opening: buildRow(null, openingOf),
            movements: movementNames.map((name, rowIndex) =>
                buildRow(name, (entry) => movementValuesOf(entry)[rowIndex] ?? null),
            ),
            closing: buildRow(null, closingOf),
        };
    }

    /** The year comes from the CLOSING date so off-calendar business years are labelled consistently. */
    public static buildEmsPeriodCaption(
        businessYearLabel: string,
        isFullYear: boolean | undefined,
        openingDate: string | undefined,
        closingDate: string | undefined,
    ): string {
        if (isFullYear === true) {
            const year = closingDate?.match(/\d{4}/)?.[0] ?? '';
            return `${businessYearLabel} ${year}`.trim();
        }
        if (openingDate != null && closingDate != null) {
            return `${openingDate} – ${closingDate}`;
        }
        return closingDate ?? openingDate ?? '';
    }
}
